// =============================================================================
// useAuth Hook - Dual-method authentication with TanStack Query
// =============================================================================
// Phase 5: Frontend Implementation
// Supports phone (SMS) and email (OTP) authentication
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi, type SendCodeRequest, type VerifyCodeRequest, type SignupRequest } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

// =============================================================================
// Query Keys
// =============================================================================

export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
};

// =============================================================================
// useCurrentUser Hook - Get current authenticated user
// =============================================================================

export function useCurrentUser() {
  const { setAuthenticated, setFeatures, setDiag, logout, step } = useAuthStore();

  return useQuery({
    queryKey: authKeys.me(),
    queryFn: async () => {
      const response = await authApi.getMe();
      if (response.success) {
        setAuthenticated(response.user, response.organization);
        // Per-environment feature flags + session diagnostics both ride the
        // /auth/me response outside the typed schema (like _diag/_dx).
        const raw = response as unknown as Record<string, unknown>;
        const features = (raw.features as { formsBridge?: boolean } | undefined) || {};
        setFeatures({ formsBridge: !!features.formsBridge });
        setDiag(
          (raw._diag as import('@/stores/auth').DiagState) || null,
          !!(raw._dx),
        );
        return response;
      }
      throw new Error(response.error?.message || 'Failed to get user');
    },
    // Only fetch if we think we're authenticated
    enabled: step === 'AUTHENTICATED',
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: {
      onError: () => {
        // Session expired or invalid
        logout();
      },
    },
  });
}

// =============================================================================
// useSendCode Hook - Request verification code (phone or email)
// =============================================================================

export function useSendCode() {
  const { setPendingIdentifier, setError, setLoading } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (data: SendCodeRequest) => {
      setLoading(true);
      const response = await authApi.sendCode(data);
      return { response, identifier: data.identifier, identifierType: data.identifierType };
    },
    onSuccess: ({ response, identifier, identifierType }) => {
      setLoading(false);
      if (response.success) {
        setPendingIdentifier({
          type: identifierType || (identifier.includes('@') ? 'email' : 'phone'),
          value: identifier,
          maskedValue: response.maskedIdentifier,
        });
        // Preserve returnTo through the verify step so it survives store wipes,
        // tab reloads, and navigation glitches. The Zustand store is still set
        // by LoginPage as a fallback.
        const currentParams = new URLSearchParams(window.location.search);
        const returnTo = currentParams.get('returnTo');
        const verifyUrl = returnTo
          ? `/verify?returnTo=${encodeURIComponent(returnTo)}`
          : '/verify';
        navigate(verifyUrl);
      } else if (response.error) {
        setError(response.error.message);
      }
    },
    onError: (error: ApiError) => {
      setLoading(false);
      setError(error.message || 'Failed to send verification code');
    },
  });
}

// Legacy alias for useSendCode
export function useLogin() {
  return useSendCode();
}

// =============================================================================
// useVerifyCode Hook - Verify 6-digit code
// =============================================================================
// v5.1: Added returnTo support for post-login redirect
// =============================================================================

export function useVerifyCode() {
  const { setAuthenticated, setSignupToken, setError, setLoading, consumeReturnTo } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: VerifyCodeRequest) => {
      setLoading(true);
      const response = await authApi.verifyCode(data);
      return response;
    },
    onSuccess: (response) => {
      setLoading(false);
      if (!response.success) {
        // Error response
        setError(response.error.message);
        return;
      }

      if (response.flow === 'login') {
        // Existing user - authenticated
        // v5.1: Consume returnTo BEFORE setAuthenticated to avoid race condition.
        // PublicRoute wrapper redirects authenticated users to "/" on re-render,
        // overriding navigate(returnTo). Use window.location.href to bypass React.
        // v5.3: Prefer returnTo from URL query param (more robust than store —
        // survives Zustand persist failures, tab reloads, page refreshes).
        // Fall back to store value if URL param is missing.
        const urlParams = new URLSearchParams(window.location.search);
        const urlReturnTo = urlParams.get('returnTo');
        const storeReturnTo = consumeReturnTo();
        const returnTo = urlReturnTo || storeReturnTo;

        setAuthenticated(response.user, response.organization);
        queryClient.invalidateQueries({ queryKey: authKeys.me() });

        if (returnTo && returnTo.startsWith('/')) {
          window.location.href = returnTo;
        } else {
          navigate('/buyers');
        }
      } else if (response.flow === 'signup') {
        // New user - needs to complete signup
        // Don't consume returnTo yet - it will be used after signup completes.
        // Preserve returnTo in the signup URL so /signup can pick it up even
        // if the store gets cleared.
        setSignupToken(
          response.signupToken,
          response.signupTokenExpiresAt,
          response.verifiedIdentifier,
          response.verifiedIdentifierType
        );
        const urlParams = new URLSearchParams(window.location.search);
        const returnTo = urlParams.get('returnTo');
        navigate(returnTo ? `/signup?returnTo=${encodeURIComponent(returnTo)}` : '/signup');
      }
    },
    onError: (error: ApiError) => {
      setLoading(false);
      setError(error.message || 'Verification failed');
    },
  });
}

// =============================================================================
// useSignup Hook - Complete user registration
// =============================================================================
// v5.1: Added returnTo support for post-signup redirect to shared property
// =============================================================================

export function useSignup() {
  const { setAuthenticated, setError, setLoading, consumeReturnTo } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SignupRequest) => {
      setLoading(true);
      const response = await authApi.signup(data);
      return response;
    },
    onSuccess: (response) => {
      setLoading(false);
      if (response.success) {
        // v5.1: Consume returnTo BEFORE setAuthenticated to avoid race condition.
        // setAuthenticated() triggers re-render of PublicRoute wrapper, which
        // redirects authenticated users to "/" — overriding navigate(returnTo).
        // Using window.location.href bypasses React's render cycle entirely.
        // v5.3: Prefer URL query param over store value (more robust).
        const urlParams = new URLSearchParams(window.location.search);
        const urlReturnTo = urlParams.get('returnTo');
        const storeReturnTo = consumeReturnTo();
        const returnTo = urlReturnTo || storeReturnTo;

        setAuthenticated(response.user, response.organization);
        queryClient.invalidateQueries({ queryKey: authKeys.me() });

        if (returnTo && returnTo.startsWith('/')) {
          window.location.href = returnTo;
        } else {
          navigate('/buyers');
        }
      } else {
        setError(response.error.message);
      }
    },
    onError: (error: ApiError) => {
      setLoading(false);
      setError(error.message || 'Signup failed');
    },
  });
}

// =============================================================================
// useLogout Hook - Sign out
// =============================================================================

export function useLogout() {
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      logout();
      queryClient.clear();
      navigate('/login');
    },
    onError: (error: ApiError) => {
      console.error('Logout failed:', error.message);
      // Clear anyway on client side
      logout();
      queryClient.clear();
      navigate('/login');
    },
  });
}

// =============================================================================
// useResendCode Hook - Resend verification code
// =============================================================================

export function useResendCode() {
  const { pendingIdentifier, setError, setLoading } = useAuthStore();

  return useMutation({
    mutationFn: async () => {
      if (!pendingIdentifier) {
        throw new Error('No pending identifier');
      }
      setLoading(true);
      const response = await authApi.sendCode({
        identifier: pendingIdentifier.value,
        identifierType: pendingIdentifier.type,
      });
      return response;
    },
    onSuccess: (response) => {
      setLoading(false);
      if (!response.success && response.error) {
        setError(response.error.message);
      }
    },
    onError: (error: ApiError) => {
      setLoading(false);
      setError(error.message || 'Failed to resend code');
    },
  });
}

// =============================================================================
// Password Authentication Hooks (v1, 2026-09)
// =============================================================================

/**
 * Shared success path for password login + MFA verification — mirrors the
 * useVerifyCode 'login' branch exactly (returnTo consumption order matters;
 * see the v5.1/v5.3 comments there).
 */
function finishPasswordAuth(
  response: import('@/lib/auth').PasswordLoginOkResponse,
  helpers: {
    setAuthenticated: ReturnType<typeof useAuthStore.getState>['setAuthenticated'];
    consumeReturnTo: () => string | null;
    queryClient: ReturnType<typeof useQueryClient>;
    navigate: ReturnType<typeof useNavigate>;
  }
): void {
  const urlParams = new URLSearchParams(window.location.search);
  const urlReturnTo = urlParams.get('returnTo');
  const storeReturnTo = helpers.consumeReturnTo();
  const returnTo = urlReturnTo || storeReturnTo;

  helpers.setAuthenticated(response.user, response.organization);
  helpers.queryClient.invalidateQueries({ queryKey: authKeys.me() });

  if (returnTo && returnTo.startsWith('/')) {
    window.location.href = returnTo;
  } else {
    helpers.navigate('/buyers');
  }
}

/**
 * Password login. onMfaRequired fires instead of navigation when the account
 * has two-step verification enabled.
 */
export function usePasswordLogin(callbacks?: {
  onMfaRequired?: (info: { maskedPhone: string; codeExpiresAt: string }) => void;
}) {
  const { setAuthenticated, setError, setLoading, consumeReturnTo } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      setLoading(true);
      return authApi.loginPassword(data);
    },
    onSuccess: (response) => {
      setLoading(false);
      if (response.status === 'mfa_required') {
        callbacks?.onMfaRequired?.({
          maskedPhone: response.maskedPhone,
          codeExpiresAt: response.codeExpiresAt,
        });
        return;
      }
      finishPasswordAuth(response, { setAuthenticated, consumeReturnTo, queryClient, navigate });
    },
    onError: (error: ApiError) => {
      setLoading(false);
      setError(error.message || 'Sign in failed');
    },
  });
}

/**
 * MFA second step after password login.
 */
export function useVerifyMfa() {
  const { setAuthenticated, setError, setLoading, consumeReturnTo } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { email: string; code: string }) => {
      setLoading(true);
      return authApi.verifyMfa(data);
    },
    onSuccess: (response) => {
      setLoading(false);
      finishPasswordAuth(response, { setAuthenticated, consumeReturnTo, queryClient, navigate });
    },
    onError: (error: ApiError) => {
      setLoading(false);
      setError(error.message || 'Verification failed');
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => authApi.forgotPassword(email),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (data: { email: string; token: string; newPassword: string }) =>
      authApi.resetPassword(data),
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { currentPassword?: string; newPassword: string }) =>
      authApi.changePassword(data),
    onSuccess: () => {
      // hasPassword flips on first set — refresh /auth/me
      queryClient.invalidateQueries({ queryKey: authKeys.me() });
    },
  });
}

export function useSetMfa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => authApi.setMfa(enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.me() });
    },
  });
}

// =============================================================================
// useAuth Hook - Convenience hook combining all auth functionality
// =============================================================================

export function useAuth() {
  const store = useAuthStore();
  const currentUser = useCurrentUser();
  const sendCodeMutation = useSendCode();
  const verifyMutation = useVerifyCode();
  const signupMutation = useSignup();
  const logoutMutation = useLogout();
  const resendMutation = useResendCode();

  return {
    // State
    user: store.user,
    organization: store.organization,
    isAuthenticated: store.isAuthenticated(),
    step: store.step,
    pendingIdentifier: store.pendingIdentifier,
    signupToken: store.signupToken,
    verifiedIdentifier: store.verifiedIdentifier,
    verifiedIdentifierType: store.verifiedIdentifierType,
    error: store.error,

    // Queries
    isLoading: currentUser.isLoading || store.isLoading,
    isError: currentUser.isError,

    // Mutations
    sendCode: sendCodeMutation.mutate,
    sendCodeAsync: sendCodeMutation.mutateAsync,
    isSendingCode: sendCodeMutation.isPending,

    // Legacy aliases
    login: sendCodeMutation.mutate,
    loginAsync: sendCodeMutation.mutateAsync,
    isLoggingIn: sendCodeMutation.isPending,

    verify: verifyMutation.mutate,
    verifyAsync: verifyMutation.mutateAsync,
    isVerifying: verifyMutation.isPending,

    signup: signupMutation.mutate,
    signupAsync: signupMutation.mutateAsync,
    isSigningUp: signupMutation.isPending,

    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,

    resendCode: resendMutation.mutate,
    isResending: resendMutation.isPending,

    // Actions
    setError: store.setError,
    reset: store.reset,
  };
}
